import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ToastTypes } from '../../../shared/services/abstract/toast.service.abstract';
import { BottomNavButton } from '../../../shared/bottom-nav/bottom-nav.component';
import {
  BottomNavService,
  BottomNavUpdateButtonsList,
  BottomNavUpdateButtonState,
  BottomNavUpdateState,
} from '../../../shared/bottom-nav/bottom-nav.service';
import { ToastService } from '../../../shared/services/toast.service';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { AuditSettings, MicrosoftGeneralSettings, QuotingProvider } from '../../../shared/models/generated/ziroModels';
import {
  ZiroFieldComponentConfig,
  ZiroFieldConfig,
  ZiroFormConfig,
  ZiroFormsValidationState,
} from '../../../forms/ziro-forms-models';
import { ZiroFormStateService } from '../../../forms/ziro-form-state.service';
import { ButtonStyles, ButtonTypes } from '../../../button/button.component';
import { BreadcrumbsService } from '../../../shared/breadcrumbs/breadcrumbs.service';
import {
  HtmlCheckboxType,
  HtmlSwitchSize,
  ZiroCheckboxConfig,
} from '../../../forms/fields/checkbox/ziro-checkbox.component';
import { HtmlInputType, ZiroTextConfig } from '../../../forms/fields/text/ziro-text.component';
import { combineLatest, forkJoin, Observable, Subscriber, Subscription } from 'rxjs';
import { ZiroFormAbstractDirective } from '../../../forms/ziro-form-abstract.directive';
import { MicrosoftGeneralSettingsResource } from '../../resources/microsoft-general-settings.resource';
import { AuditSettingsContext } from '../../contexts/audit-settings.context';
import { first, tap } from 'rxjs/operators';

interface MicrosoftGeneralSettingsFormData extends MicrosoftGeneralSettings {
  daysToKeepAuditRecords: number;
}

@Component({
  selector: 'ziro-microsoft-general-settings',
  templateUrl: './microsoft-general-settings.component.html',
  styleUrls: ['../../admin-page.scss'],
  providers: [MicrosoftGeneralSettingsResource],
  standalone: false,
})
export class MicrosoftGeneralSettingsComponent
  extends ZiroFormAbstractDirective<MicrosoftGeneralSettings, MicrosoftGeneralSettingsFormData>
  implements OnInit, OnDestroy
{
  protected _ziroFormStateService: ZiroFormStateService;
  private _toastService = inject(ToastService);
  private _microsoftGeneralSettingsResource = inject(MicrosoftGeneralSettingsResource);
  private _bottomNavService = inject(BottomNavService);
  private _breadcrumbsService = inject(BreadcrumbsService);
  private _auditSettingsContext = inject(AuditSettingsContext);

  isLoading = true;
  ziroIcons = ZiroIcons;
  bottomNavButtons = [
    {
      id: 'microsoft-general-settings-save-button',
      dataAutomation: 'microsoft-general-settings-save-button',
      label: 'tkey;admin.ui.save',
      buttonClass: ButtonStyles.PRIMARY,
      state: {
        pending: false,
        buttonDisableState: {
          disabled: false,
          tooltipKey: '',
        },
      },
      icon: this.ziroIcons.SAVE_SUCCESS,
      type: ButtonTypes.SUBMIT,
      submitSubject: this._validateAndSubmitSource,
    },
  ] as BottomNavButton[];
  formConfig = {
    fields: {
      daysToKeepAuditRecords: {
        required: true,
        dataAutomation: 'days-to-keep-audit-records',
        label: 'tkey;admin.system.microsoft.general_settings.days_to_keep.label',
        componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.NUMBER }),
        validation: [
          {
            validator: (val: number) => (val >= 1 ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID),
            message: 'tkey;admin.system.microsoft.general_settings.days_to_keep.positive.error.text',
          },
        ],
        options: {
          content: 'tkey;admin.system.microsoft.general_settings.days_to_keep.content',
        },
      },
      zpcUrl: {
        dataAutomation: 'zpc-url',
        label: 'tkey;admin.microsoft.general_settings.form.zpc_url.label',
        componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.TEXT }),
        options: {
          content: 'tkey;admin.microsoft.general_settings.form.zpc_url.content',
        },
      },
      showAssociatedDevicesOn360View: {
        dataAutomation: 'show-associated-devices',
        label: 'tkey;admin.microsoft.general_settings.form.show_associated_devices.label',
        componentConfig: new ZiroCheckboxConfig({ checkboxType: HtmlCheckboxType.SWITCH, size: HtmlSwitchSize.LG }),
        options: {
          content: 'tkey;admin.microsoft.general_settings.form.show_associated_devices.content',
        },
      },
    },
  } as ZiroFormConfig;
  auditSettings: AuditSettings;

  private _subscriptions = new Subscription();

  ngOnInit(): void {
    this._breadcrumbsService.updateBreadcrumbs([{ label: 'tkey;admin.microsoft.general_settings.title' }]);
    this._bottomNavService.dispatch(new BottomNavUpdateButtonsList(this.bottomNavButtons));

    const settingsSub = combineLatest([this._microsoftGeneralSettingsResource.get(), this._auditSettingsContext.state$])
      .pipe(
        first(),
        tap(([generalSettings, auditSettings]) => {
          this.auditSettings = auditSettings;
          this.entitySource.next(generalSettings);
          this.isLoading = false;
        }),
      )
      .subscribe();
    this._subscriptions.add(settingsSub);

    const formSubmittedSub = this._validateAndSubmitSource.subscribe(() =>
      this._setBottomNavValidationError(!this.isFormValid()),
    );
    this._subscriptions.add(formSubmittedSub);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this._subscriptions.unsubscribe();
    this._breadcrumbsService.clearBreadcrumbs();
  }

  isCheckBox(config: ZiroFieldConfig<ZiroFieldComponentConfig>): boolean {
    return config.componentConfig instanceof ZiroCheckboxConfig;
  }

  isTextInput(config: ZiroFieldConfig<ZiroFieldComponentConfig>): boolean {
    return config.componentConfig instanceof ZiroTextConfig;
  }

  // Used to keep the order of the keys-value pairs as we iterate formConfig.fields using 'keyvalue' pipe
  returnZero: () => number = () => 0;

  toFormData = (entity: MicrosoftGeneralSettings): MicrosoftGeneralSettingsFormData => {
    return {
      daysToKeepAuditRecords: this.formData.daysToKeepAuditRecords || this.auditSettings?.daysToKeepAuditRecords,
      showAssociatedDevicesOn360View: entity.showAssociatedDevicesOn360View,
      zpcUrl: entity.zpcUrl,
      quotingProvider: entity.quotingProvider,
    };
  };

  toEntity = (formData: MicrosoftGeneralSettingsFormData): MicrosoftGeneralSettings => {
    return {
      showAssociatedDevicesOn360View: formData.showAssociatedDevicesOn360View,
      zpcUrl: formData.zpcUrl,
      quotingProvider: QuotingProvider.NONE,
    } as MicrosoftGeneralSettings;
  };

  protected submit(): Observable<void> {
    return this._saveGeneralSettings();
  }

  private _saveGeneralSettings(): Observable<void> {
    return new Observable((subscriber: Subscriber<void>) => {
      this._setBottomNavValidationError(false);
      this._setBottomNavPending(true);

      const auditSettingsPayload = {
        daysToKeepAuditRecords: this.formData.daysToKeepAuditRecords,
      };
      forkJoin([
        this._microsoftGeneralSettingsResource.put(this.entity),
        this._auditSettingsContext.update(auditSettingsPayload),
      ]).subscribe(() => {
        this._setBottomNavPending(false);

        this._toastService.push(
          ToastTypes.SUCCESS,
          this.ziroIcons.SAVE_SUCCESS,
          'tkey;shared.toast.save.success.title',
          'tkey;admin.microsoft.general_settings.toast.message',
        );

        subscriber.next(null);
        subscriber.complete();
      });
    });
  }

  private _setBottomNavValidationError(hasError: boolean): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateState({
        hasValidationError: hasError,
      }),
    );
  }

  private _setBottomNavPending(setting: boolean): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: 'microsoft-general-settings-save-button',
        state: {
          pending: setting,
        },
      }),
    );
  }
}
